import { QueriesOptions } from "@tanstack/react-query/build/lib/useQueries";
import {
  PayeeRequirement,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryKey } from "@ws-react-query";

import { sharedRequirementsQueriesConfig } from "../utils";

/**
 * How long to wait before re-reading a data-source document that is still
 * mid-flight on the backend.
 *
 * A requirement is not finished the moment the user's action returns: HelloSign
 * reports "signed" before the request transitions, and an uploaded file sits in
 * AI validation for a while. Invalidating once at that point refetches the
 * *pre-transition* document, so without polling the screen shows the old status
 * until the user reloads.
 */
const NON_TERMINAL_DATA_SOURCE_POLL_MS = 5000;

/**
 * Predicate deciding whether a fetched data-source document is still moving.
 * Receives `undefined` while the first read is in flight.
 */
export type IsDataSourceNonTerminal<T> = (document: T | undefined) => boolean;

const getDataSourceQuery = <T>(
  getQueryKey: (dataSourceId: string) => WSQueryKey,
  requirement: PayeeRequirement,
  fetchFunction: (dataSourceId: string) => Promise<T>,
  isNonTerminal?: IsDataSourceNonTerminal<T>
) => {
  const { dataSourceId } = requirement;
  const replacedDataSourceId = requirement.replacedRequirement?.dataSourceId;

  const refetchInterval = isNonTerminal
    ? (document: T | undefined) =>
        isNonTerminal(document) ? NON_TERMINAL_DATA_SOURCE_POLL_MS : false
    : undefined;

  const queries = [
    dataSourceId
      ? {
          queryKey: getQueryKey(dataSourceId),
          queryFn: () => fetchFunction(dataSourceId),
          ...sharedRequirementsQueriesConfig,
          refetchInterval
        }
      : undefined,
    replacedDataSourceId
      ? {
          queryKey: getQueryKey(replacedDataSourceId),
          queryFn: () => fetchFunction(replacedDataSourceId),
          ...sharedRequirementsQueriesConfig,
          refetchInterval
        }
      : undefined
  ].filter(Boolean) as NonNullable<QueriesOptions<any>>[];

  return queries;
};

export const getDataSourceQueries = <T>(
  getQueryKey: (dataSourceId: string) => WSQueryKey,
  requirements: PayeeRequirement[],
  fetchFunction: (dataSourceId: string) => Promise<T>,
  requirementType: RequirementType,
  isNonTerminal?: IsDataSourceNonTerminal<T>
): NonNullable<QueriesOptions<any>>[] => {
  const queries = requirements
    .filter(
      requirement =>
        requirement.type === requirementType && requirement.dataSourceId
    )
    .map(requirement =>
      getDataSourceQuery(getQueryKey, requirement, fetchFunction, isNonTerminal)
    )
    .flat();

  return queries;
};
